/**
 * Light global client state (Zustand) — UI/control state that many features
 * read: the selected environment, global time window, sidebar collapse, and
 * the live-polling toggle. Server data lives in TanStack Query, not here.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { env } from '@/shared/config/env';
import type { TimeWindow } from '@/shared/types';

export type AppEnv = 'dev' | 'staging' | 'prod';

interface AppState {
  environment: AppEnv;
  window: TimeWindow;
  sidebarCollapsed: boolean;
  liveEnabled: boolean;
  setEnvironment: (e: AppEnv) => void;
  setWindow: (w: TimeWindow) => void;
  toggleSidebar: () => void;
  setLiveEnabled: (v: boolean) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      environment: env.VITE_DEFAULT_ENV,
      window: '1h',
      sidebarCollapsed: false,
      liveEnabled: true,
      setEnvironment: (environment) => set({ environment }),
      setWindow: (window) => set({ window }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setLiveEnabled: (liveEnabled) => set({ liveEnabled }),
    }),
    {
      name: 'fincera-app',
      partialize: (s) => ({
        environment: s.environment,
        window: s.window,
        sidebarCollapsed: s.sidebarCollapsed,
        liveEnabled: s.liveEnabled,
      }),
    },
  ),
);

/**
 * Convenience selector: the analytics scope (env + window) most hooks need.
 * Selects each field atomically — returning a fresh object *from the selector*
 * would make useSyncExternalStore see a new snapshot every render (infinite loop
 * under Zustand v5). Building the object here, outside the selectors, is safe.
 */
export function useScope() {
  const environment = useAppStore((s) => s.environment);
  const window = useAppStore((s) => s.window);
  return { environment, window };
}
